import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import styles from './LeaderboardList.module.css';
import { formatCurrency, formatNumber } from '../../utils/formatters';

/**
 * LeaderboardList Component
 * Displays a compact ranked list with progress indicators
 */
function LeaderboardList({
    title,
    icon: Icon,
    rows = [],
    onRowClick = null,
    valueLabel = 'Revenue',
    valueFormatter = (v) => formatCurrency(v, { compact: true }),
    showOrders = true,
    showVolume = false,
    variant = 'default', // 'default' | 'warning'
    collapsibleOnMobile = true,
}) {
    const [isCollapsed, setIsCollapsed] = useState(false);

    if (!rows || rows.length === 0) {
        return null;
    }

    const maxValue = Math.max(...rows.map(r => r.gmv || r.value || 0));

    return (
        <div className={`${styles.leaderboard} ${styles[variant]}`}>
            <div
                className={styles.header}
                onClick={() => collapsibleOnMobile && setIsCollapsed(!isCollapsed)}
            >
                {Icon && (
                    <div className={`${styles.iconWrapper} ${styles[variant]}`}>
                        <Icon size={18} />
                    </div>
                )}
                <h3 className={styles.title}>{title}</h3>
                {collapsibleOnMobile && (
                    <button className={styles.collapseBtn}>
                        {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                )}
            </div>

            <div className={`${styles.content} ${isCollapsed ? styles.collapsed : ''}`}>
                <div className={styles.list}>
                    {rows.map((row, index) => {
                        const value = row.gmv || row.value || 0;
                        const progressPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;

                        return (
                            <div
                                key={row.id || row.code || index}
                                className={`${styles.row} ${onRowClick ? styles.clickable : ''}`}
                                onClick={() => onRowClick && onRowClick(row)}
                            >
                                <div className={styles.rank}>
                                    <span className={`${styles.rankNumber} ${index < 3 ? styles.top3 : ''}`}>
                                        {index + 1}
                                    </span>
                                </div>

                                <div className={styles.info}>
                                    <div className={styles.nameRow}>
                                        <span className={styles.name}>{row.name}</span>
                                        <span className={styles.value}>{valueFormatter(value)}</span>
                                    </div>
                                    <div className={styles.progressBar}>
                                        <div
                                            className={`${styles.progress} ${styles[variant]}`}
                                            style={{ width: `${progressPercent}%` }}
                                        />
                                    </div>
                                    <div className={styles.meta}>
                                        {showOrders && (
                                            <span>{formatNumber(row.orders || 0)} orders</span>
                                        )}
                                        {showVolume && (
                                            <span>{formatNumber(row.volume || 0)} items</span>
                                        )}
                                        {row.growth !== undefined && (
                                            <span className={row.growth >= 0 ? styles.positive : styles.negative}>
                                                {row.growth >= 0 ? '+' : ''}{row.growth.toFixed(1)}%
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default LeaderboardList;
